import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Collapse,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Chip,
  Avatar,
  FormControlLabel,
  Switch,
  Card,
  CardContent,
  CardMedia,
  Divider,
  Stack,
  Badge,
  ToggleButtonGroup,
  ToggleButton,
  useTheme,
  useMediaQuery,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  Search as SearchIcon,
  KeyboardArrowDown as ArrowDownIcon,
  KeyboardArrowUp as ArrowUpIcon,
  Refresh as RefreshIcon,
  ViewList as ViewListIcon,
  ViewModule as ViewModuleIcon,
  Inventory as InventoryIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  FilterList as FilterListIcon,
  ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';
import {
  getProductsInventoryDisplay,
  getRelatedProductsInventory,
  getWarehousesForInventoryDisplay,
  getProductAttributesForInventoryDisplay,
  getProductTypesForInventoryDisplay,
  ProductInventoryDisplay,
  Warehouse as WarehouseType,
  ProductAttribute as ProductAttributeType
} from '../services/inventoryDisplayService';

const InventoryDisplayPage: React.FC = () => {
  // استخدام useMediaQuery للكشف عن حجم الشاشة
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [products, setProducts] = useState<ProductInventoryDisplay[]>([]);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [relatedProductsCache, setRelatedProductsCache] = useState<{
    [key: string]: ProductInventoryDisplay[];
  }>({});
  const [loadingRelated, setLoadingRelated] = useState<Set<string>>(new Set());
  
  // الخصائص من النظام
  const [productAttributes, setProductAttributes] = useState<ProductAttributeType[]>([]);
  const [loadingAttributes, setLoadingAttributes] = useState(false);
  
  // المستودعات
  const [warehouses, setWarehouses] = useState<WarehouseType[]>([]);
  const [loadingWarehouses, setLoadingWarehouses] = useState(false);
  
  // أنواع المنتجات
  const [productTypes, setProductTypes] = useState<string[]>([]);
  const [loadingProductTypes, setLoadingProductTypes] = useState(false);
  
  // خيار تفعيل فلتر المنتجات المرتبطة
  const [showRelatedFilter, setShowRelatedFilter] = useState(false);
  
  // نوع العرض (table أو cards)
  const [viewMode, setViewMode] = useState<'table' | 'cards'>('cards');
  
  // حالة توسيع الفلاتر للجوال
  const [filtersExpanded, setFiltersExpanded] = useState(!isMobile);

  // الفلاتر الأساسية
  const [filters, setFilters] = useState<{
    productType: string;
    search: string;
    warehouseId: string;
    [key: string]: string;
  }>({
    productType: '',
    search: '',
    warehouseId: ''
  });
  
  // فلاتر المنتجات المرتبطة
  const [relatedFilters, setRelatedFilters] = useState<{
    productType: string;
    search: string;
    warehouseId: string;
    [key: string]: string;
  }>({
    productType: '',
    search: '',
    warehouseId: ''
  });

  // جلب الخصائص من النظام - باستخدام endpoint خاص بصفحة عرض المخزون
  useEffect(() => {
    const fetchAttributes = async () => {
      setLoadingAttributes(true);
      try {
        const attributes = await getProductAttributesForInventoryDisplay();
        setProductAttributes(attributes || []);
      } catch (err) {
        console.error('Error fetching product attributes:', err);
      } finally {
        setLoadingAttributes(false);
      }
    };

    fetchAttributes();
  }, []);

  // جلب المستودعات - باستخدام endpoint خاص بصفحة عرض المخزون
  useEffect(() => {
    const fetchWarehouses = async () => {
      setLoadingWarehouses(true);
      try {
        const warehousesList = await getWarehousesForInventoryDisplay();
        console.log('📦 Warehouses List:', warehousesList);
        setWarehouses(warehousesList);
      } catch (err: any) {
        console.error('❌ Error fetching warehouses:', err);
        setWarehouses([]);
      } finally {
        setLoadingWarehouses(false);
      }
    };

    fetchWarehouses();
  }, []);

  // جلب أنواع المنتجات - باستخدام endpoint خاص بصفحة عرض المخزون
  useEffect(() => {
    const fetchProductTypes = async () => {
      setLoadingProductTypes(true);
      try {
        const types = await getProductTypesForInventoryDisplay();
        setProductTypes(types || []);
      } catch (err) {
        console.error('Error fetching product types:', err);
      } finally {
        setLoadingProductTypes(false);
      }
    };

    fetchProductTypes();
  }, []);

  // جلب المنتجات عند تحميل الصفحة
  const handleSearch = async () => {
    // التحقق من وجود فلتر واحد على الأقل
    const hasAnyFilter =
      filters.productType ||
      filters.search ||
      filters.warehouseId ||
      Object.keys(filters).some(key => key.startsWith('attr_') && filters[key]);

    if (!hasAnyFilter) {
      setError('الرجاء اختيار فلتر واحد على الأقل قبل البحث');
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      const attributes: { [key: string]: string } = {};
      
      // جمع الخصائص من الفلاتر
      productAttributes.forEach(attr => {
        const filterKey = `attr_${attr.name}`;
        if (filters[filterKey]) {
          attributes[attr.name] = filters[filterKey];
        }
      });

      const response = await getProductsInventoryDisplay({
        productType: filters.productType || undefined,
        search: filters.search || undefined,
        warehouseId: filters.warehouseId || undefined,
        attributes: Object.keys(attributes).length > 0 ? attributes : undefined,
        page: 1,
        limit: 50
      });

      let fetchedProducts = response.products || [];
      
      // إذا كان فلتر المنتجات المرتبطة مفعل، تطبيق الفلتر
      if (showRelatedFilter) {
        const filteredProducts: ProductInventoryDisplay[] = [];
        
        for (const product of fetchedProducts) {
          try {
            const productId = product.productId || product._id;
            const relatedResponse = await getRelatedProductsInventory(productId);
            const relatedProducts = relatedResponse.relatedProducts || [];
            
            // تطبيق فلاتر المنتجات المرتبطة
            const filteredRelated = relatedProducts.filter(related => {
              // فلتر نوع المنتج
              if (relatedFilters.productType && related.productType !== relatedFilters.productType) {
                return false;
              }
              
              // فلتر المستودع
              if (relatedFilters.warehouseId) {
                const hasWarehouse = related.inventory?.some(inv => inv.warehouseId === relatedFilters.warehouseId);
                if (!hasWarehouse) return false;
              }
              
              // فلتر البحث
              if (relatedFilters.search && !related.name.toLowerCase().includes(relatedFilters.search.toLowerCase())) {
                return false;
              }
              
              // فلتر الخصائص
              for (const attr of productAttributes) {
                const filterKey = `attr_${attr.name}`;
                if (relatedFilters[filterKey] && related.attributes?.[attr.name] !== relatedFilters[filterKey]) {
                  return false;
                }
              }
              
              return true;
            });
            
            // إذا كان هناك منتجات مرتبطة تطابق الفلتر، أضف المنتج الأصلي
            if (filteredRelated.length > 0) {
              filteredProducts.push(product);
              // حفظ المنتجات المرتبطة المفلترة في الـ cache
              relatedProductsCache[product._id] = filteredRelated;
              // لا نفتح المنتجات المرتبطة تلقائياً - يفتحها المستخدم يدوياً
            }
          } catch (err) {
            console.error(`Error fetching related products for ${product._id}:`, err);
          }
        }
        
        fetchedProducts = filteredProducts;
        setRelatedProductsCache({...relatedProductsCache});
        // لا نضع expandedRows هنا - نتركها فارغة ليفتحها المستخدم يدوياً
      }
      
      setProducts(fetchedProducts);
    } catch (err) {
      console.error('Error fetching products:', err);
      setError('فشل في تحميل البيانات. الرجاء المحاولة مرة أخرى.');
    } finally {
      setLoading(false);
    }
  };

  // توسيع/طي صف المنتجات المرتبطة
  const toggleRow = async (product: ProductInventoryDisplay) => {
    const rowId = product._id;
    const productId = product.productId || product._id; // استخدام productId إذا كان متغير
    
    const newExpanded = new Set(expandedRows);
    
    if (newExpanded.has(rowId)) {
      newExpanded.delete(rowId);
    } else {
      newExpanded.add(rowId);
      
      // جلب المنتجات المرتبطة إذا لم تكن محملة
      if (!relatedProductsCache[rowId]) {
        const newLoadingRelated = new Set(loadingRelated);
        newLoadingRelated.add(rowId);
        setLoadingRelated(newLoadingRelated);
        
        try {
          console.log('🔍 Fetching related products for:', productId);
          const response = await getRelatedProductsInventory(productId);
          console.log('📦 Related products response:', response);
          setRelatedProductsCache({
            ...relatedProductsCache,
            [rowId]: response.relatedProducts || []
          });
        } catch (err) {
          console.error('Error fetching related products:', err);
          setRelatedProductsCache({
            ...relatedProductsCache,
            [rowId]: []
          });
        } finally {
          const newLoadingRelated = new Set(loadingRelated);
          newLoadingRelated.delete(rowId);
          setLoadingRelated(newLoadingRelated);
        }
      }
    }
    
    setExpandedRows(newExpanded);
  };

  // إعادة تعيين الفلاتر
  const handleReset = () => {
    const resetFilters: any = {
      productType: '',
      search: '',
      warehouseId: ''
    };
    
    const resetRelatedFilters: any = {
      productType: '',
      search: '',
      warehouseId: ''
    };
    
    // إعادة تعيين جميع فلاتر الخصائص
    productAttributes.forEach(attr => {
      resetFilters[`attr_${attr.name}`] = '';
      resetRelatedFilters[`attr_${attr.name}`] = '';
    });
    
    setFilters(resetFilters);
    setRelatedFilters(resetRelatedFilters);
    setProducts([]);
    setExpandedRows(new Set());
    setRelatedProductsCache({});
  };

  // دالة للتحقق من توفر الكمية في كلا المستودعين (الفلتر الأول والثاني)
  const checkBothWarehousesAvailability = (product: ProductInventoryDisplay, isRelated: boolean): boolean => {
    // يجب أن يكون كلا الفلترين محددين
    if (!filters.warehouseId || !relatedFilters.warehouseId || !showRelatedFilter) {
      return false;
    }

    if (isRelated) {
      // للمنتج المرتبط: التحقق من توفر الكمية في المستودع من الفلتر الثاني
      const relatedWarehouseInventory = product.inventory?.find(
        inv => inv.warehouseId === relatedFilters.warehouseId
      );
      return relatedWarehouseInventory ? relatedWarehouseInventory.quantity > 0 : false;
    } else {
      // للمنتج الأساسي: التحقق من توفر الكمية في المستودع من الفلتر الأول
      const mainWarehouseInventory = product.inventory?.find(
        inv => inv.warehouseId === filters.warehouseId
      );
      
      if (!mainWarehouseInventory || mainWarehouseInventory.quantity <= 0) {
        return false;
      }

      // التحقق من وجود منتجات مرتبطة
      const relatedProducts = relatedProductsCache[product._id];
      if (!relatedProducts || relatedProducts.length === 0) {
        return false;
      }

      // التحقق من أن أي منتج مرتبط لديه كمية > 0 في المستودع من الفلتر الثاني
      const hasRelatedWithQuantity = relatedProducts.some(related => {
        const relatedWarehouseInventory = related.inventory?.find(
          inv => inv.warehouseId === relatedFilters.warehouseId
        );
        return relatedWarehouseInventory && relatedWarehouseInventory.quantity > 0;
      });

      return hasRelatedWithQuantity;
    }
  };

  // عرض صف المنتج
  const renderProductRow = (product: ProductInventoryDisplay, isRelated = false) => {
    // التحقق من توفر الكمية في كلا المستودعين
    const hasAvailabilityInBothWarehouses = checkBothWarehousesAvailability(product, isRelated);
    
    // تحديد لون الخلفية
    let backgroundColor = 'white';
    if (hasAvailabilityInBothWarehouses) {
      backgroundColor = '#e8f5e9'; // أخضر فاتح
    } else if (isRelated) {
      backgroundColor = '#f5f5f5'; // رمادي فاتح للمنتجات المرتبطة
    }
    
    return (
    <React.Fragment key={product._id}>
      <TableRow sx={{ backgroundColor }}>
        {!isRelated && (
          <TableCell>
            <IconButton
              size="small"
              onClick={() => toggleRow(product)}
            >
              {expandedRows.has(product._id) ? <ArrowUpIcon /> : <ArrowDownIcon />}
            </IconButton>
          </TableCell>
        )}
        {isRelated && <TableCell />}
        
        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {product.mainImage && (
              <Avatar
                src={product.mainImage}
                alt={product.name}
                variant="rounded"
                sx={{ width: 40, height: 40 }}
              />
            )}
            <Box>
              <Typography variant="body2" sx={{ fontWeight: isRelated ? 'normal' : 'bold' }}>
                {isRelated && '→ '}{product.name}
              </Typography>
              {product.productType && (
                <Chip
                  label={product.productType}
                  size="small"
                  sx={{ mt: 0.5 }}
                />
              )}
            </Box>
          </Box>
        </TableCell>

        <TableCell>
          {product.attributes && Object.entries(product.attributes).map(([key, value]) => (
            <Chip
              key={key}
              label={`${key}: ${value}`}
              size="small"
              sx={{ mr: 0.5, mb: 0.5 }}
            />
          ))}
        </TableCell>

        <TableCell>
          {product.inventory && product.inventory.length > 0 ? (
            <Box>
              {product.inventory
                .filter(inv => {
                  // فلترة المستودعات بناءً على الفلتر المحدد
                  if (isRelated && relatedFilters.warehouseId) {
                    return inv.warehouseId === relatedFilters.warehouseId;
                  } else if (!isRelated && filters.warehouseId) {
                    return inv.warehouseId === filters.warehouseId;
                  }
                  // إذا لم يكن هناك فلتر محدد، عرض جميع المستودعات
                  return true;
                })
                .map((inv, idx) => (
                <Box key={idx} sx={{ mb: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                    {inv.warehouseName}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    الكمية المتاحة: {inv.quantity}
                  </Typography>
                </Box>
              ))}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              لا توجد بيانات مخزون
            </Typography>
          )}
        </TableCell>
      </TableRow>

      {/* صف المنتجات المرتبطة */}
      {!isRelated && (
        <TableRow>
          <TableCell colSpan={4} sx={{ p: 0, border: 0 }}>
            <Collapse in={expandedRows.has(product._id)} timeout="auto" unmountOnExit>
              <Box sx={{ p: 2, backgroundColor: '#fafafa' }}>
                {loadingRelated.has(product._id) ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : relatedProductsCache[product._id]?.length > 0 ? (
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell width="50px" />
                        <TableCell>المنتج المرتبط</TableCell>
                        <TableCell>الخصائص</TableCell>
                        <TableCell>كميات المخزون</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {relatedProductsCache[product._id].map((relatedProduct) =>
                        renderProductRow(relatedProduct, true)
                      )}
                    </TableBody>
                  </Table>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                    لا توجد منتجات مرتبطة
                  </Typography>
                )}
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </React.Fragment>
    );
  };

  // عرض بطاقة المنتج
  const renderProductCard = (product: ProductInventoryDisplay, isRelated = false) => {
    const hasAvailabilityInBothWarehouses = checkBothWarehousesAvailability(product, isRelated);
    
    // تحديد لون الحدود والخلفية
    let borderColor = '#e0e0e0';
    let backgroundColor = 'white';
    if (hasAvailabilityInBothWarehouses) {
      borderColor = '#4caf50';
      backgroundColor = '#e8f5e9';
    } else if (isRelated) {
      backgroundColor = '#f5f5f5';
    }

    // فلترة المستودعات
    const filteredInventory = product.inventory?.filter(inv => {
      if (isRelated && relatedFilters.warehouseId) {
        return inv.warehouseId === relatedFilters.warehouseId;
      } else if (!isRelated && filters.warehouseId) {
        return inv.warehouseId === filters.warehouseId;
      }
      return true;
    }) || [];

    return (
      <Card
        key={product._id}
        sx={{
          height: '100%',
          border: `2px solid ${borderColor}`,
          backgroundColor,
          transition: 'all 0.3s ease',
          '&:hover': {
            boxShadow: 6,
            transform: 'translateY(-4px)'
          },
          position: 'relative'
        }}
      >
        {hasAvailabilityInBothWarehouses && (
          <Box
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              zIndex: 1
            }}
          >
            <CheckCircleIcon sx={{ color: '#4caf50', fontSize: 32 }} />
          </Box>
        )}

        {product.mainImage && (
          <CardMedia
            component="img"
            height="180"
            image={product.mainImage}
            alt={product.name}
            sx={{ objectFit: 'cover' }}
          />
        )}

        <CardContent>
          <Stack spacing={2}>
            {/* اسم المنتج ونوعه */}
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                {isRelated && '→ '}{product.name}
              </Typography>
              {product.productType && (
                <Chip
                  label={product.productType}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              )}
            </Box>

            <Divider />

            {/* الخصائص */}
            {product.attributes && Object.keys(product.attributes).length > 0 && (
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold', display: 'block', mb: 1 }}>
                  الخصائص:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {Object.entries(product.attributes).map(([key, value]) => (
                    <Chip
                      key={key}
                      label={`${key}: ${value}`}
                      size="small"
                      sx={{ fontSize: '0.75rem' }}
                    />
                  ))}
                </Box>
              </Box>
            )}

            <Divider />

            {/* كميات المخزون */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <InventoryIcon fontSize="small" color="action" />
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                  كميات المخزون:
                </Typography>
              </Box>
              
              {filteredInventory.length > 0 ? (
                <Stack spacing={1.5}>
                  {filteredInventory.map((inv, idx) => (
                    <Paper
                      key={idx}
                      elevation={0}
                      sx={{
                        p: 1.5,
                        backgroundColor: '#f5f5f5',
                        border: '1px solid #e0e0e0'
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                        📦 {inv.warehouseName}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                          الكمية المتاحة:
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#4caf50' }}>
                          {inv.quantity}
                        </Typography>
                      </Box>
                    </Paper>
                  ))}
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  لا توجد بيانات مخزون
                </Typography>
              )}
            </Box>

            {/* زر المنتجات المرتبطة */}
            {!isRelated && (
              <Button
                variant="outlined"
                size="small"
                startIcon={expandedRows.has(product._id) ? <ArrowUpIcon /> : <ArrowDownIcon />}
                onClick={() => toggleRow(product)}
                sx={{ mt: 1 }}
              >
                {expandedRows.has(product._id) ? 'إخفاء' : 'عرض'} المنتجات المرتبطة
                {relatedProductsCache[product._id]?.length > 0 && (
                  <Badge
                    badgeContent={relatedProductsCache[product._id].length}
                    color="primary"
                    sx={{ ml: 1 }}
                  />
                )}
              </Button>
            )}
          </Stack>
        </CardContent>
      </Card>
    );
  };

  // عرض البطاقات
  const renderCardsView = () => (
    <Grid container spacing={isMobile ? 2 : 3}>
      {products.map((product) => (
        <React.Fragment key={product._id}>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            {renderProductCard(product)}
          </Grid>
          
          {/* المنتجات المرتبطة */}
          {expandedRows.has(product._id) && (
            <Grid item xs={12}>
              <Collapse in={expandedRows.has(product._id)} timeout="auto" unmountOnExit>
                <Paper sx={{ p: 2, backgroundColor: '#fafafa' }}>
                  {loadingRelated.has(product._id) ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                      <CircularProgress size={24} />
                    </Box>
                  ) : relatedProductsCache[product._id]?.length > 0 ? (
                    <>
                      <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ArrowDownIcon />
                        المنتجات المرتبطة بـ {product.name}
                      </Typography>
                      <Grid container spacing={2}>
                        {relatedProductsCache[product._id].map((relatedProduct) => (
                          <Grid item xs={12} sm={6} md={4} lg={3} key={relatedProduct._id}>
                            {renderProductCard(relatedProduct, true)}
                          </Grid>
                        ))}
                      </Grid>
                    </>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', p: 2 }}>
                      لا توجد منتجات مرتبطة
                    </Typography>
                  )}
                </Paper>
              </Collapse>
            </Grid>
          )}
        </React.Fragment>
      ))}
    </Grid>
  );

  return (
    <Box sx={{ width: '100%' }}>
      {/* منطقة الفلاتر */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>نوع المنتج</InputLabel>
              <Select
                value={filters.productType}
                label="نوع المنتج"
                onChange={(e) => setFilters({ ...filters, productType: e.target.value })}
              >
                <MenuItem value="">الكل</MenuItem>
                {productTypes.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>المستودع</InputLabel>
              <Select
                value={filters.warehouseId}
                label="المستودع"
                onChange={(e) => setFilters({ ...filters, warehouseId: e.target.value })}
                disabled={loadingWarehouses}
              >
                <MenuItem value="">الكل</MenuItem>
                {warehouses.map((warehouse) => (
                  <MenuItem key={warehouse._id} value={warehouse._id}>
                    {warehouse.name} {warehouse.location && `- ${warehouse.location}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* عرض الخصائص الديناميكية من النظام */}
          {loadingAttributes ? (
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '56px' }}>
                <CircularProgress size={24} />
              </Box>
            </Grid>
          ) : (
            productAttributes.map((attr) => (
              <Grid item xs={12} sm={6} md={3} key={attr._id}>
                <FormControl fullWidth>
                  <InputLabel>{attr.name}</InputLabel>
                  <Select
                    value={filters[`attr_${attr.name}`] || ''}
                    label={attr.name}
                    onChange={(e) => setFilters({ ...filters, [`attr_${attr.name}`]: e.target.value })}
                  >
                    <MenuItem value="">الكل</MenuItem>
                    {attr.values.map((value) => (
                      <MenuItem key={value} value={value}>
                        {value}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            ))
          )}

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="البحث بالاسم"
              placeholder="ابحث عن منتج..."
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleSearch();
                }
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  startIcon={<SearchIcon />}
                  onClick={handleSearch}
                  disabled={loading}
                >
                  بحث
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={handleReset}
                  disabled={loading}
                >
                  إعادة تعيين
                </Button>
                
                <FormControlLabel
                  control={
                    <Switch
                      checked={showRelatedFilter}
                      onChange={(e) => setShowRelatedFilter(e.target.checked)}
                      color="primary"
                    />
                  }
                  label="تفعيل فلتر المنتجات المرتبطة"
                  sx={{ ml: 2 }}
                />
              </Box>

              {/* زر التبديل بين عرض الجدول والبطاقات */}
              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={(e, newMode) => {
                  if (newMode !== null) {
                    setViewMode(newMode);
                  }
                }}
                size="small"
                sx={{ backgroundColor: 'white' }}
              >
                <ToggleButton value="cards" aria-label="عرض البطاقات">
                  <ViewModuleIcon sx={{ mr: 1 }} />
                  بطاقات
                </ToggleButton>
                <ToggleButton value="table" aria-label="عرض الجدول">
                  <ViewListIcon sx={{ mr: 1 }} />
                  جدول
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Grid>
        </Grid>

        {/* فلتر المنتجات المرتبطة */}
        {showRelatedFilter && (
          <Box sx={{ mt: 3, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              فلتر المنتجات المرتبطة
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth>
                  <InputLabel>نوع المنتج</InputLabel>
                  <Select
                    value={relatedFilters.productType}
                    label="نوع المنتج"
                    onChange={(e) => setRelatedFilters({ ...relatedFilters, productType: e.target.value })}
                  >
                    <MenuItem value="">الكل</MenuItem>
                    {productTypes.map((type) => (
                      <MenuItem key={type} value={type}>
                        {type}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth>
                  <InputLabel>المستودع</InputLabel>
                  <Select
                    value={relatedFilters.warehouseId}
                    label="المستودع"
                    onChange={(e) => setRelatedFilters({ ...relatedFilters, warehouseId: e.target.value })}
                    disabled={loadingWarehouses}
                  >
                    <MenuItem value="">الكل</MenuItem>
                    {warehouses.map((warehouse) => (
                      <MenuItem key={warehouse._id} value={warehouse._id}>
                        {warehouse.name} {warehouse.location && `- ${warehouse.location}`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* عرض الخصائص الديناميكية للمنتجات المرتبطة */}
              {productAttributes.map((attr) => (
                <Grid item xs={12} sm={6} md={3} key={attr._id}>
                  <FormControl fullWidth>
                    <InputLabel>{attr.name}</InputLabel>
                    <Select
                      value={relatedFilters[`attr_${attr.name}`] || ''}
                      label={attr.name}
                      onChange={(e) => setRelatedFilters({ ...relatedFilters, [`attr_${attr.name}`]: e.target.value })}
                    >
                      <MenuItem value="">الكل</MenuItem>
                      {attr.values.map((value) => (
                        <MenuItem key={value} value={value}>
                          {value}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              ))}

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="البحث بالاسم (المنتجات المرتبطة)"
                  placeholder="ابحث عن منتج مرتبط..."
                  value={relatedFilters.search}
                  onChange={(e) => setRelatedFilters({ ...relatedFilters, search: e.target.value })}
                />
              </Grid>
            </Grid>
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* عرض النتائج */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : products.length > 0 ? (
          viewMode === 'cards' ? (
            renderCardsView()
          ) : (
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell width="50px">مرتبط</TableCell>
                    <TableCell>المنتج</TableCell>
                    <TableCell>الخصائص</TableCell>
                    <TableCell>كميات المخزون</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {products.map((product) => renderProductRow(product))}
                </TableBody>
              </Table>
            </TableContainer>
          )
        ) : (
          <Box sx={{ textAlign: 'center', p: 4 }}>
            <Typography variant="body1" color="text.secondary">
              {Object.values(filters).some(val => val !== '')
                ? 'لا توجد نتائج. جرب تغيير الفلاتر.'
                : 'استخدم الفلاتر أعلاه للبحث عن المنتجات'}
            </Typography>
          </Box>
        )}
    </Box>
  );
};

export default InventoryDisplayPage;
